/* eslint-disable no-nested-ternary */

import React from "react";
import { fromJS } from "immutable";
import { OverlayTrigger, ListGroup, Form } from "react-bootstrap";
import { rankingOrRecommendationScore } from "containers/Results/FunctionLib";
import {
  getNewDate,
  testScoreRangeHasTextScores,
  determineCustomFilterOptions,
} from "utils/helperFunctions";

/* ---------- Helper functions and objects for sorting ----------- */

export const sortingOptions = [
  { value: "testName", label: "Test" },
  { value: "name", label: "Name" },
  { value: "createdDate", label: "Date Created" },
  { value: "createdBy", label: "Created By" },
  { value: "jobsAssociated", label: "Jobs Associated" },
];

const sortMethodToPropertyNameObj = {
  testName: "testId",
  name: "testBaselineName",
  createdDate: "createdOn",
  createdBy: "creator",
  jobsAssociated: "jobCount",
};

export const handleSorting = (sortMethod, userA, userB) => {
  const propertyName = sortMethodToPropertyNameObj[sortMethod];
  let valueA;
  let valueB;
  if (sortMethod === "testName") {
    valueA = userA.get(propertyName)
      ? testIdToTestAbbreviation[userA.get(propertyName)]
      : userA.get("testMakerTestName")
        ? userA.get("testMakerTestName")
        : "";
    valueB = userB.get(propertyName)
      ? testIdToTestAbbreviation[userB.get(propertyName)]
      : userB.get("testMakerTestName")
        ? userB.get("testMakerTestName")
        : "";
  } else {
    valueA = userA.get(propertyName) || "";
    valueB = userB.get(propertyName) || "";
  }
  switch (propertyName) {
    case "testId":
    case "testBaselineName":
    case "creator":
      return valueA.localeCompare(valueB);
    case "createdOn":
      if (getNewDate(valueA) === getNewDate(valueB)) {
        return getNewDate(valueB) - getNewDate(valueA);
      }
      if (valueA === 0) {
        return 1;
      }
      if (valueB === 0) {
        return -1;
      }
      return getNewDate(valueB) - getNewDate(valueA);
    case "jobCount":
      return valueB - valueA;
    default:
      return userA.get("name").localeCompare(userB.get("name"));
  }
};

const sortedAndDisplayedScoreRanges = (
  scoreRanges,
  sortMethod,
  currentPage,
  sizePerPage
) =>
  scoreRanges
    .sort((a, b) => handleSorting(sortMethod, a, b))
    .slice((currentPage - 1) * sizePerPage, currentPage * sizePerPage);

/* ----------- getter functions used in index.js ----------- */

export const getFilteredSearchedScoreRanges = (
  scoreRanges,
  scoreRangeSearchTerm
) => {
  const filteredSearchedScoreRanges =
    scoreRanges !== null && Object.keys(scoreRanges).length > 0
      ? scoreRanges
          .filter(
            (scoreRange) =>
              `${scoreRange.get("testBaselineName")}`
                .toUpperCase()
                .indexOf(scoreRangeSearchTerm.toUpperCase()) >= 0
          )
          .filter(
            (scoreRange) =>
              scoreRange.get("scoreRange") !== null &&
              scoreRange.get("createdBy") !== null
          )
      : fromJS({});
  return filteredSearchedScoreRanges;
};

export const getCreatedByFilterUsers = (filteredByTestVersionScoreRanges) => {
  const createdByFilterUsers = {};
  filteredByTestVersionScoreRanges.map((scoreRange) => {
    if (
      !Object.keys(createdByFilterUsers).includes(
        scoreRange.get("createdBy") && scoreRange.get("createdBy") === 0
      )
    ) {
      createdByFilterUsers[scoreRange.get("createdBy")] = {
        userAccountId: scoreRange.get("createdBy"),
        userName: `${scoreRange.get("creator")}`,
      };
    }
  });
  return createdByFilterUsers;
};

export const getScoreRangesToUse = (
  createdByFilter,
  filteredByTestVersionScoreRanges,
  sortMethod,
  currentPage,
  sizePerPage,
  filteredByCreatedBy
) => {
  const scoreRangesToUse =
    createdByFilter.length > 0
      ? sortedAndDisplayedScoreRanges(
          filteredByCreatedBy,
          sortMethod,
          currentPage,
          sizePerPage
        )
      : sortedAndDisplayedScoreRanges(
          filteredByTestVersionScoreRanges,
          sortMethod,
          currentPage,
          sizePerPage
        );
  return scoreRangesToUse;
};

/* ---------- Some Test IDs Helper objects  ----------- */

export const testIdToTestNameMap = {
  10: "Criteria Cognitive Aptitude Test",
  12: "Criteria Basic Skills Test",
  13: "Criteria Personality Inventory",
  14: "MiniCog Rapid Assessment Battery",
  15: "Customer Service Aptitude Profile",
  16: "Wiesen Test of Mechanical Aptitude",
  17: "Typing Test",
  18: "Ten Key Test",
  19: "Microsoft Excel 2003",
  20: "Microsoft Word 2003",
  21: "Microsoft Excel 2007",
  22: "Microsoft Word 2007",
  23: "Criteria Attention Skills Test",
  24: "Sales Achievement Predictor",
  29: "Employee Personality Profile",
  30: "Computer Literacy and Internet Knowledge",
  31: "Workplace Productivity Profile",
  32: "Microsoft Excel 2010",
  33: "Microsoft Word 2010",
  34: "Employee Personality Profile",
  35: "Microsoft Excel 2013",
  36: "Microsoft Word 2013",
  37: "Universal Cognitive Aptitude Test",
  38: "Microsoft PowerPoint 2013",
  39: "Microsoft Excel 2016",
  40: "Microsoft Word 2016",
  41: "Microsoft PowerPoint 2016",
  42: "General Aptitude Mobile Evaluation",
  43: "Criteria Mechanical Reasoning Assessment",
  44: "Emotify",
  45: "Cognify",
  46: "Workplace Safety Profile",
  47: "Criteria Cognitive Aptitude Test",
  48: "Illustrait",
  49: "Computer Literacy and Internet Knowledge",
  50: "Workplace Alignment Assessment",
  51: "Emotify v2",
  52: "UCognify",
  53: "Excel 365",
  54: "Word 365",
  55: "PowerPoint 365",
  57: "Criteria Language Proficiency Test - English",
  59: "Criteria Think Reason Solve Express",
  // LAST LINE - DO NOT MOVE THIS COMMENT testIdToTestNameMap
};

export const testIdToTestAbbreviation = {
  10: "CCAT",
  12: "CBST",
  13: "CPI",
  14: "MRAB",
  15: "CSAP",
  16: "WTMA",
  17: "TT",
  18: "TKT",
  19: "Excel",
  20: "Word",
  21: "Excel",
  22: "Word",
  23: "CAST",
  24: "SalesAP",
  30: "CLIK",
  31: "WPP",
  32: "Excel",
  33: "Word",
  34: "EPP",
  35: "Excel",
  36: "Word",
  37: "UCAT",
  38: "PowerPoint",
  39: "Excel",
  40: "Word",
  41: "PowerPoint",
  42: "GAME",
  43: "CMRA",
  44: "Emotify",
  45: "Cognify",
  46: "WSP",
  47: "CCAT",
  48: "Illustrait",
  49: "CLIK",
  50: "WAA",
  51: "Emotify v2",
  52: "UCognify",
  53: "Excel 365",
  54: "Word 365",
  55: "PowerPoint 365",
  57: "CLPT-EN",
  "57_express": "CLPTE-EN",
  59: "CTRSE",
  // LAST LINE - DO NOT MOVE THIS COMMENT testIdToTestAbbreviation
};

export const testIdToTraitMap = {
  10: { raw: 1, percentile: 2, verbal: 3, math: 4, spatial: 5 },
  12: { raw: 6, percentile: 7, math: 8, verbal: 9 },
  13: { AGREE: 10, CONSC: 11, EXTRA: 12, OPEN: 13, STAB: 14 },
  14: {
    percentile: 15,
    ATT: 16,
    MEM: 17,
    REA: 18,
    DA: 19,
    VIG: 20,
    FIL: 21,
    VWM: 22,
    SWM: 23,
    VIS: 24,
    LOG: 25,
    IO: 26,
    PRT: 27,
  },
  15: {
    raw: 28,
    SAL: 29,
    CC: 30,
    CLS: 31,
    ACH: 32,
    MOT: 33,
    CMP: 34,
    GO: 35,
    PLN: 36,
    INI: 37,
    TMP: 38,
    MGT: 39,
    AST: 40,
    PDL: 41,
    EXT: 42,
    COP: 43,
    RLX: 44,
    PAT: 45,
    SCN: 46,
  },
  16: { raw: 47, percentile: 48 },
  17: { raw: 49, percentile: 50 },
  18: { raw: 51 },
  21: { raw: 53, percentile: 54 },
  22: { raw: 55, percentile: 56 },
  23: { percentile: 57, DA: 58, VIG: 59, FIL: 60, PRT: 61 },
  24: {
    raw: 62,
    SAL: 63,
    CC: 64,
    CLS: 65,
    ACH: 66,
    MOT: 67,
    CMP: 68,
    GO: 69,
    PLN: 70,
    INI: 71,
    TMP: 72,
    MGT: 73,
    AST: 74,
    PDL: 75,
    EXT: 76,
    COP: 77,
    RLX: 78,
    PAT: 79,
    SCN: 80,
  },
  29: {
    ACH: 81,
    MOT: 82,
    CMP: 83,
    MGT: 84,
    AST: 85,
    EXT: 86,
    COP: 87,
    PAT: 88,
    SCN: 89,
    CON: 90,
  },
  30: { raw: 91, ranking: 92 },
  31: { raw: 93, CON: 94, PER: 95, HON: 96, THEFT: 97 },
  32: { raw: 98, percentile: 99 },
  33: { raw: 100, percentile: 101 },
  34: {
    ACH: 102,
    MOT: 103,
    CMP: 104,
    MGT: 105,
    AST: 106,
    EXT: 107,
    COP: 108,
    PAT: 109,
    SCN: 110,
    CON: 111,
    OPEN: 112,
    STAB: 113,
    Manager: 114,
    AccountingFinance: 115,
    Administrative_Assistant: 116,
    Analyst: 117,
    Bank_Teller: 118,
    Collections: 119,
    Customer_Service: 120,
    Front_DeskReception: 121,
    Medical_Assistant: 122,
    ProductionManufacturing: 123,
    ProgrammerDeveloper: 124,
    Sales: 125,
  },
  35: { raw: 126, percentile: 127 },
  36: { raw: 128, percentile: 129 },
  37: {
    raw: 130,
    percentile: 131,
    logic: 132,
    math: 133,
    detail: 134,
    spatial: 135,
  },
  38: { raw: 136, percentile: 137 },
  39: { raw: 138, percentile: 139 },
  40: { raw: 140, percentile: 141 },
  41: { raw: 142, percentile: 143 },
  42: { percentile: 144, detail: 145, verbal: 146, math: 147 },
  43: { raw: 148, percentile: 149 },
  44: {
    percentile: 150,
    perceivingPercentile: 151,
    understandPercentile: 152,
  },
  45: {
    percentile: 153,
    problemSolvingPercentile: 154,
    math: 155,
    verbalKnowledgePercentile: 156,
  },
  46: {
    percentile: 157,
    safety: 158,
    risk: 159,
    stress: 160,
    drug: 161,
    violence: 162,
  },
  48: { percentile: 177 },
  49: { raw: 175, ranking: 176 },
  50: { percentile: 166, raw: 167 },
  51: {
    percentile: 168,
    perceivingPercentile: 169,
    understandPercentile: 170,
    managingPercentile: 171,
  },
  52: {
    percentile: 172,
    problemSolvingPercentile: 173,
    math: 174,
  },
  53: { score: 178, proficiency: 179 },
  54: { score: 180, proficiency: 181 },
  55: {
    score: 182,
    proficiency: 183,
  },
  57: {
    score: 184,
    proficiency: 185,
  },
  59: { percentile: 195 },
  // LAST LINE - DO NOT MOVE THIS COMMENT testIdToTraitMap
};

const traitIdToTest = {
  1: { traitAbbrev: "raw", testId: 10 },
  2: { traitAbbrev: "percentile", testId: 10 },
  3: { traitAbbrev: "verbal", testId: 10 },
  4: { traitAbbrev: "math", testId: 10 },
  5: { traitAbbrev: "spatial", testId: 10 },
  6: { traitAbbrev: "raw", testId: 12 },
  7: { traitAbbrev: "percentile", testId: 12 },
  8: { traitAbbrev: "math", testId: 12 },
  9: { traitAbbrev: "verbal", testId: 12 },
  10: { traitAbbrev: "AGREE", testId: 13 },
  11: { traitAbbrev: "CONSC", testId: 13 },
  12: { traitAbbrev: "EXTRA", testId: 13 },
  13: { traitAbbrev: "OPEN", testId: 13 },
  14: { traitAbbrev: "STAB", testId: 13 },
  15: { traitAbbrev: "percentile", testId: 14 },
  16: { traitAbbrev: "ATT", testId: 14 },
  17: { traitAbbrev: "MEM", testId: 14 },
  18: { traitAbbrev: "REA", testId: 14 },
  19: { traitAbbrev: "DA", testId: 14 },
  20: { traitAbbrev: "VIG", testId: 14 },
  21: { traitAbbrev: "FIL", testId: 14 },
  22: { traitAbbrev: "VWM", testId: 14 },
  23: { traitAbbrev: "SWM", testId: 14 },
  24: { traitAbbrev: "VIS", testId: 14 },
  25: { traitAbbrev: "LOG", testId: 14 },
  26: { traitAbbrev: "IO", testId: 14 },
  27: { traitAbbrev: "PRT", testId: 14 },
  28: { traitAbbrev: "raw", testId: 15 },
  29: { traitAbbrev: "SAL", testId: 15 },
  30: { traitAbbrev: "CC", testId: 15 },
  31: { traitAbbrev: "CLS", testId: 15 },
  32: { traitAbbrev: "ACH", testId: 15 },
  33: { traitAbbrev: "MOT", testId: 15 },
  34: { traitAbbrev: "CMP", testId: 15 },
  35: { traitAbbrev: "GO", testId: 15 },
  36: { traitAbbrev: "PLN", testId: 15 },
  37: { traitAbbrev: "INI", testId: 15 },
  38: { traitAbbrev: "TMP", testId: 15 },
  39: { traitAbbrev: "MGT", testId: 15 },
  40: { traitAbbrev: "AST", testId: 15 },
  41: { traitAbbrev: "PDL", testId: 15 },
  42: { traitAbbrev: "EXT", testId: 15 },
  43: { traitAbbrev: "COP", testId: 15 },
  44: { traitAbbrev: "RLX", testId: 15 },
  45: { traitAbbrev: "PAT", testId: 15 },
  46: { traitAbbrev: "SCN", testId: 15 },
  47: { traitAbbrev: "raw", testId: 16 },
  48: { traitAbbrev: "percentile", testId: 16 },
  49: { traitAbbrev: "raw", testId: 17 },
  50: { traitAbbrev: "percentile", testId: 17 },
  51: { traitAbbrev: "raw", testId: 18 },
  53: { traitAbbrev: "raw", testId: 21 },
  54: { traitAbbrev: "percentile", testId: 21 },
  55: { traitAbbrev: "raw", testId: 22 },
  56: { traitAbbrev: "percentile", testId: 22 },
  57: { traitAbbrev: "percentile", testId: 23 },
  58: { traitAbbrev: "DA", testId: 23 },
  59: { traitAbbrev: "VIG", testId: 23 },
  60: { traitAbbrev: "FIL", testId: 23 },
  61: { traitAbbrev: "PRT", testId: 23 },
  62: { traitAbbrev: "raw", testId: 24 },
  63: { traitAbbrev: "SAL", testId: 24 },
  64: { traitAbbrev: "CC", testId: 24 },
  65: { traitAbbrev: "CLS", testId: 24 },
  66: { traitAbbrev: "ACH", testId: 24 },
  67: { traitAbbrev: "MOT", testId: 24 },
  68: { traitAbbrev: "CMP", testId: 24 },
  69: { traitAbbrev: "GO", testId: 24 },
  70: { traitAbbrev: "PLN", testId: 24 },
  71: { traitAbbrev: "INI", testId: 24 },
  72: { traitAbbrev: "TMP", testId: 24 },
  73: { traitAbbrev: "MGT", testId: 24 },
  74: { traitAbbrev: "AST", testId: 24 },
  75: { traitAbbrev: "PDL", testId: 24 },
  76: { traitAbbrev: "EXT", testId: 24 },
  77: { traitAbbrev: "COP", testId: 24 },
  78: { traitAbbrev: "RLX", testId: 24 },
  79: { traitAbbrev: "PAT", testId: 24 },
  80: { traitAbbrev: "SCN", testId: 24 },
  81: { traitAbbrev: "ACH", testId: 29 },
  82: { traitAbbrev: "MOT", testId: 29 },
  83: { traitAbbrev: "CMP", testId: 29 },
  84: { traitAbbrev: "MGT", testId: 29 },
  85: { traitAbbrev: "AST", testId: 29 },
  86: { traitAbbrev: "EXT", testId: 29 },
  87: { traitAbbrev: "COP", testId: 29 },
  88: { traitAbbrev: "PAT", testId: 29 },
  89: { traitAbbrev: "SCN", testId: 29 },
  90: { traitAbbrev: "CON", testId: 29 },
  91: { traitAbbrev: "raw", testId: 30 },
  92: { traitAbbrev: "ranking", testId: 30 },
  93: { traitAbbrev: "raw", testId: 31 },
  94: { traitAbbrev: "CON", testId: 31 },
  95: { traitAbbrev: "PER", testId: 31 },
  96: { traitAbbrev: "HON", testId: 31 },
  97: { traitAbbrev: "THEFT", testId: 31 },
  98: { traitAbbrev: "raw", testId: 32 },
  99: { traitAbbrev: "percentile", testId: 32 },
  100: { traitAbbrev: "raw", testId: 33 },
  101: { traitAbbrev: "percentile", testId: 33 },
  102: { traitAbbrev: "ACH", testId: 34 },
  103: { traitAbbrev: "MOT", testId: 34 },
  104: { traitAbbrev: "CMP", testId: 34 },
  105: { traitAbbrev: "MGT", testId: 34 },
  106: { traitAbbrev: "AST", testId: 34 },
  107: { traitAbbrev: "EXT", testId: 34 },
  108: { traitAbbrev: "COP", testId: 34 },
  109: { traitAbbrev: "PAT", testId: 34 },
  110: { traitAbbrev: "SCN", testId: 34 },
  111: { traitAbbrev: "CON", testId: 34 },
  112: { traitAbbrev: "OPEN", testId: 34 },
  113: { traitAbbrev: "STAB", testId: 34 },
  114: { traitAbbrev: "Manager", testId: 34 },
  115: { traitAbbrev: "AccountingFinance", testId: 34 },
  116: { traitAbbrev: "Administrative_Assistant", testId: 34 },
  117: { traitAbbrev: "Analyst", testId: 34 },
  118: { traitAbbrev: "Bank_Teller", testId: 34 },
  119: { traitAbbrev: "Collections", testId: 34 },
  120: { traitAbbrev: "Customer_Service", testId: 34 },
  121: { traitAbbrev: "Front_DeskReception", testId: 34 },
  122: { traitAbbrev: "Medical_Assistant", testId: 34 },
  123: { traitAbbrev: "ProductionManufacturing", testId: 34 },
  124: { traitAbbrev: "ProgrammerDeveloper", testId: 34 },
  125: { traitAbbrev: "Sales", testId: 34 },
  126: { traitAbbrev: "raw", testId: 35 },
  127: { traitAbbrev: "percentile", testId: 35 },
  128: { traitAbbrev: "raw", testId: 36 },
  129: { traitAbbrev: "percentile", testId: 36 },
  130: { traitAbbrev: "raw", testId: 37 },
  131: { traitAbbrev: "percentile", testId: 37 },
  132: { traitAbbrev: "logic", testId: 37 },
  133: { traitAbbrev: "math", testId: 37 },
  134: { traitAbbrev: "detail", testId: 37 },
  135: { traitAbbrev: "spatial", testId: 37 },
  136: { traitAbbrev: "raw", testId: 38 },
  137: { traitAbbrev: "percentile", testId: 38 },
  138: { traitAbbrev: "raw", testId: 39 },
  139: { traitAbbrev: "percentile", testId: 39 },
  140: { traitAbbrev: "raw", testId: 40 },
  141: { traitAbbrev: "percentile", testId: 40 },
  142: { traitAbbrev: "raw", testId: 41 },
  143: { traitAbbrev: "percentile", testId: 41 },
  144: { traitAbbrev: "percentile", testId: 42 },
  145: { traitAbbrev: "detail", testId: 42 },
  146: { traitAbbrev: "verbal", testId: 42 },
  147: { traitAbbrev: "math", testId: 42 },
  148: { traitAbbrev: "raw", testId: 43 },
  149: { traitAbbrev: "percentile", testId: 43 },
  150: { traitAbbrev: "percentile", testId: 44 },
  151: { traitAbbrev: "perceivingPercentile", testId: 44 },
  152: { traitAbbrev: "understandPercentile", testId: 44 },
  153: { traitAbbrev: "percentile", testId: 45 },
  154: { traitAbbrev: "problemSolvingPercentile", testId: 45 },
  155: { traitAbbrev: "math", testId: 45 },
  156: { traitAbbrev: "verbalKnowledgePercentile", testId: 45 },
  157: { traitAbbrev: "percentile", testId: 46 },
  158: { traitAbbrev: "safety", testId: 46 },
  159: { traitAbbrev: "risk", testId: 46 },
  160: { traitAbbrev: "stress", testId: 46 },
  161: { traitAbbrev: "drug", testId: 46 },
  162: { traitAbbrev: "violence", testId: 46 },
  166: { traitAbbrev: "percentile", testId: 50 },
  167: { traitAbbrev: "raw", testId: 50 },
  168: { traitAbbrev: "percentile", testId: 51 },
  169: { traitAbbrev: "perceivingPercentile", testId: 51 },
  170: { traitAbbrev: "understandPercentile", testId: 51 },
  171: { traitAbbrev: "managingPercentile", testId: 51 },
  172: { traitAbbrev: "percentile", testId: 52 },
  173: { traitAbbrev: "problemSolving", testId: 52 },
  174: { traitAbbrev: "numericalReasoning", testId: 52 },
  175: { traitAbbrev: "raw", testId: 49 },
  176: { traitAbbrev: "ranking", testId: 49 },
  177: { traitAbbrev: "percentile", testId: 48 },
  178: { traitAbbrev: "score", testId: 53 },
  179: { traitAbbrev: "proficiency", testId: 53 },
  180: { traitAbbrev: "score", testId: 54 },
  181: { traitAbbrev: "proficiency", testId: 54 },
  182: { traitAbbrev: "score", testId: 55 },
  183: { traitAbbrev: "proficiency", testId: 55 },
  184: { traitAbbrev: "score", testId: 57 },
  185: { traitAbbrev: "proficiency", testId: 57 },
  186: { traitAbbrev: "readingScore", testId: 57 },
  187: { traitAbbrev: "readingProficiency", testId: 57 },
  188: { traitAbbrev: "readingCEFR", testId: 57 },
  189: { traitAbbrev: "listeningScore", testId: 57 },
  190: { traitAbbrev: "listeningProficiency", testId: 57 },
  191: { traitAbbrev: "listeningCEFR", testId: 57 },
  192: { traitAbbrev: "writingScore", testId: 57 },
  193: { traitAbbrev: "writingProficiency", testId: 57 },
  194: { traitAbbrev: "writingCEFR", testId: 57 },
  195: { traitAbbrev: "percentile", testId: 59 },

  // LAST LINE - DO NOT MOVE THIS COMMENT traitIdToTest
};

export const rankingOrRecommendationTextToScore = {
  15: {
    "Not Recommended": 1,
    Recommended: 2,
    "Highly Recommended": 3,
    1: "Not Recommended",
    2: "Recommended",
    3: "Highly Recommended",
  },
  24: {
    "Not Recommended": 1,
    Recommended: 2,
    "Highly Recommended": 3,
    1: "Not Recommended",
    2: "Recommended",
    3: "Highly Recommended",
  },
  30: {
    "Not Proficient": 1,
    Proficient: 2,
    "Highly Proficient": 3,
    1: "Not Proficient",
    2: "Proficient",
    3: "Highly Proficient",
    // Adding these lines for Score Range Table
    0: "Not Proficient",
    14: "Proficient",
    18: "Highly Proficient",
  },
  31: {
    Low: 1,
    Medium: 2,
    High: 3,
    1: "Low",
    2: "Medium",
    3: "High",
  },
  49: {
    "Not Proficient": 1,
    Proficient: 2,
    "Highly Proficient": 3,
    1: "Not Proficient",
    2: "Proficient",
    3: "Highly Proficient",
    // Adding these lines for Score Range Table
    0: "Not Proficient",
    17: "Proficient",
    22: "Highly Proficient",
  },
  53: {
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
  },
  54: {
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
  },
  55: {
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
  },
  57: {
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
  },
  // LAST LINE - DO NOT MOVE THIS COMMENT rankingOrRecommendationTextToScore
};

export const determineDropdownValues = (traitId, testMakerTests, testId) => {
  const testInfo =
    traitId && traitIdToTest[traitId]
      ? traitIdToTest[traitId]
      : { traitAbbrev: "TM", testId };

  return determineCustomFilterOptions(testInfo.testId, testMakerTests)[3][
    testInfo.traitAbbrev
  ];
};

/* ---------- Score Ranges Form getter Functions and helper objects---------- */

export const compareCriteriaRangeTestIds = {
  10: ["By Raw Score"],
  12: ["By Raw Score"],
  37: ["By Raw Score"],
  16: ["By Raw Score"],
  42: ["By Overall Percentile"],
  43: ["By Raw Score"],
  45: ["By Overall Percentile"],
  52: ["By Overall Percentile"],
  59: ["By Overall Percentile"],
};

export const getBars = (
  testId,
  distribution,
  compareRange,
  rangeParameter,
  cellWidth,
  cellMargin,
  maxNumber,
  defaultMinValue
) => {
  // do not show the bars for the tkt. It adds lag and weirdness, due to there being scores between 0 and 15000
  if (testId !== "18") {
    return distribution.map((bar, i) => {
      const withinCompareRange =
        compareRange && i >= compareRange.min && i <= compareRange.max;
      const belowCutOffColor = withinCompareRange ? "#C8CdD6" : "#eff0f0";
      // Adjust the display based on the test and whether overall recommendation is available
      // Default to just showing the actual Score
      let scoreDisplay = i;
      // scoreRange with text scores (Ranking/Recommendation/Proficiency) should display text scores
      if (
        rankingOrRecommendationTextToScore[testId] &&
        rangeParameter.firstDropdown !== "By Raw Score" &&
        rangeParameter.firstDropdown !== "By Score" &&
        rangeParameter.secondDropdown === ""
      ) {
        scoreDisplay = rankingOrRecommendationScore(
          testId,
          i + defaultMinValue
        );
      }
      return (
        <OverlayTrigger
          // eslint-disable-next-line react/no-array-index-key
          key={i}
          placement="left"
          overlay={
            bar > 0 ? (
              <div
                style={{
                  border: "1px solid black",
                  padding: "3px",
                  backgroundColor: "#fff",
                  zIndex: 10,
                }}
              >
                {rangeParameter.secondDropdown ||
                  rangeParameter.firstDropdown.substring(3)}
                : {scoreDisplay}
                <br />
                Candidates: {bar}
              </div>
            ) : (
              <div />
            )
          }
        >
          <div
            className="distribution-cell"
            style={{
              width: `${cellWidth}%`,
              backgroundColor: withinCompareRange ? "#D3D7E0" : "",
              marginRight: `${
                i !== distribution.length - 1 ? `${cellMargin}%` : 0
              }`,
              marginLeft: `${i !== 0 ? `${cellMargin}%` : 0}`,
            }}
          >
            <div
              style={{
                height: `${((100 * bar) / maxNumber) * 0.8}%`,
                backgroundColor: belowCutOffColor,
                position: "relative",
                top: `${100 - ((100 * bar) / maxNumber) * 0.8}%`,
              }}
            />
          </div>
        </OverlayTrigger>
      );
    });
  }
  return null;
};

export const isNewerTestVersion = (testId, availableTests) => {
  const testAbbreviation = availableTests
    .find((test) => test.get("testId") === testId)
    .get("testAbbreviation");

  const testVersion = availableTests
    .find((test) => test.get("testId") === testId)
    .get("version");

  const sameTestsAvailableTests = availableTests
    .filter(
      (availableTest) =>
        testAbbreviation === availableTest.get("testAbbreviation")
    )
    .sort((a, b) => a.get("version") - b.get("version"));

  return testVersion === sameTestsAvailableTests.last().get("version");
};

// Tests which only allow 1 parameter to be added to score range
export const oneScoreRangeParameterTests = [53, 54, 55, 57,59];

/* ---------- YieldJobsModal.js helper function ---------- */

export const getAssociatedJobsList = (
  testScores,
  chosenJobIds,
  allJobs,
  toggleJobCheck
) => {
  const ActiveJobsList = [];
  const InactiveJobsList = [];
  if (testScores && chosenJobIds !== null && allJobs && allJobs.size > 0) {
    testScores
      .keySeq()
      .filter((jobId) => testScores.get(jobId).get("testScores") !== null)
      .sort((a, b) =>
        testScores
          .get(a)
          .get("jobTitle")
          .toLowerCase()
          .localeCompare(testScores.get(b).get("jobTitle").toLowerCase())
      )
      .forEach((jobId) => {
        const job = allJobs.get(jobId);
        if (job.get("jobStatus") > 0) {
          ActiveJobsList.push(
            <ListGroup.Item key={jobId} className="yield-jobs-list-item">
              <Form.Check.Input
                type="checkbox"
                id={`job-check-${jobId}`}
                checked={chosenJobIds.includes(jobId)}
                value={jobId}
                onChange={toggleJobCheck}
              />
              <div className="associated-job-title">
                <strong>{job.get("jobTitle")}</strong>
              </div>
              <div className="associated-job-description">
                {job.get("city") || job.get("state") !== ""
                  ? `${job.get("city")}, ${job.get("state")} | `
                  : null}
                Job ID: <strong>{job.get("jobCode")}</strong> | Created by:{" "}
                <strong>
                  {job.get("firstName")} {job.get("lastName")}
                </strong>
              </div>
              <hr />
            </ListGroup.Item>
          );
        } else {
          InactiveJobsList.push(
            <ListGroup.Item key={jobId} className="yield-jobs-list-item">
              <Form.Check.Input
                type="checkbox"
                id={`job-check-${jobId}`}
                checked={chosenJobIds.includes(jobId)}
                value={jobId}
                onChange={toggleJobCheck}
              />
              <div className="associated-job-title">
                <strong>{job.get("jobTitle")}</strong>
              </div>
              <div className="associated-job-description">
                {job.get("city") || job.get("state") !== ""
                  ? `${job.get("city")}, ${job.get("state")} | `
                  : null}
                Job ID: <strong>{job.get("jobCode")}</strong> | Created by:{" "}
                <strong>
                  {job.get("firstName")} {job.get("lastName")}
                </strong>
              </div>
              <hr />
            </ListGroup.Item>
          );
        }
      });
  }

  return [ActiveJobsList, InactiveJobsList];
};

/*
 *  Purpose: Get the minimum and maximum scores for a given score range.

 *  Parameters:
 *    - scoreRange: Map containing score range data
 *    - testId: Number
 *    - formatAsText: Boolean value to determine the return format
 *
 *  If formatTAsText is true, returns `({minScoreRange} - {maxScoreRange})` string
 *  Else, returns [minScoreRange, maxScoreRange] array
 *
 */
export const getMinMaxScoreRange = (scoreRange, testId, formatAsText) => {
  // Round minimum and maximum of the score range from decimals to the nearest integer value
  const minScoreRangeInteger = parseScoreRangeLimitToInteger(
    scoreRange.get("min_scoreRange")
  );

  const maxScoreRangeInteger = parseScoreRangeLimitToInteger(
    scoreRange.get("max_scoreRange")
  );

  const useTextScore = testScoreRangeHasTextScores(
    testId,
    scoreRange.get("rangeName") || scoreRange.get("scoreRangeAbbrev")
  );

  const minScoreRange = useTextScore
    ? rankingOrRecommendationTextToScore[testId][minScoreRangeInteger]
    : String(minScoreRangeInteger);

  const maxScoreRange = useTextScore
    ? rankingOrRecommendationTextToScore[testId][maxScoreRangeInteger]
    : String(maxScoreRangeInteger);

  return formatAsText
    ? `(${String(minScoreRange)} - ${String(maxScoreRange)})`
    : [minScoreRange, maxScoreRange];
};

const parseScoreRangeLimitToInteger = (scoreRange) => parseInt(scoreRange, 10);

// Linearly interpolate to position the black line within
// the valid range, which is [(cellWidth / 2)%, (100 - cellWidth / 2)%]
export const getGraphLineLeftOffset = (cellWidth, value, defaultValues) =>
  `${
    ((100 - cellWidth) * (value - defaultValues.min)) /
      (defaultValues.max - defaultValues.min) +
    cellWidth / 2
  }%`;
